import { Component, ElementRef, Input, OnInit, inject } from '@angular/core';
import { NgbActiveModal, NgbDatepickerModule } from '@ng-bootstrap/ng-bootstrap';
import { UserAction } from '../../../core/enums/user-action.enum';
import { FormBuilder, ReactiveFormsModule, Validators } from '@angular/forms';
import { TranslocoModule } from '@jsverse/transloco';
import { IWSDateTime } from '../../../core/interfaces/ws-datetime.interface';
import { CommonModule } from '@angular/common';
import { IDEA_API_SERVICE, IDropdownOption, IIdeaApiResponse } from '@ideatech/ngx-ideatech';
import { map, Observable } from 'rxjs';
import { ApiHolder } from '../../../core/classes/api-holder';
import { IPostProjectResponse } from '../../../core/interfaces/api-responses/post-project-response.interface';
import { ProjectService } from '../../../core/services/project.service';
import { AuthenticationService } from '../../../core/services/authentication.service';

@Component({
    selector: 'app-project-heading-modal',
    standalone: true,
    imports: [ReactiveFormsModule, TranslocoModule, NgbDatepickerModule, CommonModule],
    templateUrl: './project-heading-modal.component.html',
    styleUrl: './project-heading-modal.component.scss'
})
export class ProjectHeadingModalComponent implements OnInit {

    @Input() action: UserAction = UserAction.INSERT;

    private readonly projectService = inject(ProjectService);
    private readonly authenticationService = inject(AuthenticationService);
    private readonly formBuilder = inject(FormBuilder);
    protected readonly activeModal = inject(NgbActiveModal);

    protected projectHeadingForm = this.formBuilder.group({
        title: this.formBuilder.nonNullable.control("", [Validators.required]),
        idCustomer: this.formBuilder.nonNullable.control((null! as number), [Validators.required]),
        deliveryDate: this.formBuilder.nonNullable.control((null! as IWSDateTime), [Validators.required]),
        note: [""]
    });

    protected customersLookupOptions: IDropdownOption[] = [];

    ngOnInit(): void {
        this.authenticationService.getUserCustomersLookup().subscribe({
            next: (customersLookupResult => {
                this.customersLookupOptions = customersLookupResult;
                if (this.action === UserAction.EDIT) {
                    this.setCurrentProjectData();
                } else {
                    this.setDefaultValues();
                }
            }),
            error: () => {
                this.activeModal.dismiss();
            }
        })
    }

    protected onProjectHeadingFormSubmit(): void {
        if (this.projectHeadingForm.invalid) {
            this.projectHeadingForm.markAllAsTouched();
        } else {
            if (this.action === UserAction.INSERT) {
                this.saveNewProject().subscribe(resultSaveNewProject => this.activeModal.close(resultSaveNewProject))
            } else if (this.action === UserAction.EDIT) {
                this.updateProject().subscribe(() => this.activeModal.close());
            }
        }

    }

    private setCurrentProjectData(): void {
        this.projectHeadingForm.patchValue({
            title: this.projectService.project.title,
            idCustomer: this.projectService.project.idCustomer,
            deliveryDate: this.projectService.project.deliveryDate.toObject(),
            note: this.projectService.project.note
        });
    }

    private setDefaultValues(): void {
        if (this.customersLookupOptions.length === 1) {
            this.projectHeadingForm.patchValue({
                idCustomer: this.customersLookupOptions[0].key as number
            });
        }
    }

    private saveNewProject(): Observable<number | null> {
        return this.projectService.create(this.projectHeadingForm.value);
    }

    private updateProject(): Observable<void> {
        const customerBusinessName = this.customersLookupOptions.find(c => c.key === this.projectHeadingForm.value.idCustomer)!.value;
        return this.projectService.update(this.projectHeadingForm.value, customerBusinessName);
    }
}
